/**
 * EstimateEditModal - Modal for editing draft estimates
 */

import { useMemo } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "../ui/dialog";
import LineItemsEditor from "../LineItemsEditor";
import TotalsSummary from "../TotalsSummary";
import DepositConfig from "./DepositConfig";

export default function EstimateEditModal({
  isOpen,
  onOpenChange,
  editFormData,
  setEditFormData,
  taxRatePercent,
  onSubmit,
  onUpdateLineItem,
  onAddLineItem,
  onRemoveLineItem,
  onCatalogSelect,
  onFormatPrice,
}) {
  // Calculate estimate total for deposit preview
  const estimateTotal = useMemo(() => {
    if (!editFormData?.items) return 0;
    const subtotal = editFormData.items.reduce((sum, item) => {
      const qty = parseFloat(item.quantity) || 1;
      const price = parseFloat(item.price) || 0;
      return sum + qty * price;
    }, 0);
    const taxableTotal = editFormData.items.reduce((sum, item) => {
      if (!item.is_taxable) return sum;
      const qty = parseFloat(item.quantity) || 1;
      const price = parseFloat(item.price) || 0;
      return sum + qty * price;
    }, 0);
    const tax = taxableTotal * (taxRatePercent / 100);
    return subtotal + tax;
  }, [editFormData?.items, taxRatePercent]);

  // Handle deposit config changes
  const handleDepositChange = (depositData) => {
    setEditFormData({
      ...editFormData,
      deposit_enabled: depositData.deposit_enabled,
      deposit_type: depositData.deposit_type,
      deposit_value: depositData.deposit_value,
    });
  };

  if (!editFormData) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="edit-estimate-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl uppercase">
            Edit Estimate
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <form id="edit-estimate-form" onSubmit={onSubmit} className="space-y-4">
            {/* Customer (read-only) */}
            <div>
              <Label>Customer</Label>
              <Input
                value={editFormData.customer_name}
                disabled
                className="mt-1 h-12 border-2 bg-zinc-100 text-zinc-500"
              />
            </div>

            {/* Title */}
            <div>
              <Label>Title *</Label>
              <Input
                value={editFormData.title}
                onChange={(e) => setEditFormData({ ...editFormData, title: e.target.value })}
                className="mt-1 h-12 border-2"
                required
                data-testid="edit-estimate-title"
              />
            </div>

            <LineItemsEditor
              items={editFormData.items}
              onUpdateItem={onUpdateLineItem}
              onAddItem={onAddLineItem}
              onRemoveItem={onRemoveLineItem}
              onCatalogSelect={onCatalogSelect}
              onFormatPrice={onFormatPrice}
              taxRatePercent={taxRatePercent}
              testIdPrefix="edit-estimate-line"
            />

            {/* Notes */}
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={editFormData.notes}
                onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                placeholder="Terms, conditions, or additional notes..."
                className="mt-1 border-2"
                rows={3}
              />
            </div>

            <TotalsSummary items={editFormData.items} taxRatePercent={taxRatePercent} />

            {/* Deposit Configuration - secondary config section */}
            <DepositConfig
              depositEnabled={editFormData.deposit_enabled || false}
              depositType={editFormData.deposit_type}
              depositValue={editFormData.deposit_value}
              onChange={handleDepositChange}
              estimateTotal={estimateTotal}
              testIdPrefix="edit-estimate-deposit"
            />
          </form>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 sm:flex-none border-2"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-estimate-form"
            className="flex-1 sm:flex-none bg-[#FF6600] hover:bg-[#CC5200] text-white"
            disabled={!editFormData.title}
            data-testid="save-edit-estimate-btn"
          >
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
